import { useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { defaultEQM4010_R01DetailDto, EQM4010_R01DetailDto, EQM4010_R01HeaderDto } from "./Dto_EQM4010_R01";
import { defaultEQM4010_R01Filter, EQM4010_R01Filter, useEQM4010_R01Store } from "./Store_EQM4010_R01";
import {
	postEQM4010_R01,
	deleteEQM4010_R01,
	downloadExcel_EQM4010_R01,
	getEQM4010_R01,
	getEQM4010_R01AllCount,
	getEQM4010_R01DetailList,
	patchEQM4010_R01,
} from "./Api_EQM4010_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showWarning, showSuccess, ShowSuccessFetch } from "src/utils/toast/toastUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { formatDateToString } from "src/utils/date/dateUtils";

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM4010_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<EQM4010_R01DetailDto> | null>;
}

const mapDetailForSave = (detail: EQM4010_R01DetailDto) => ({
	moldCleanNo: detail.moldCleanNo,
	compCd: detail.compCd,
	moldCd: detail.moldCd,
	cleanDt: detail.cleanDt,
	cleanType: detail.cleanType,
	cleanWorker: detail.cleanWorker,
	cleanResult: detail.cleanResult,
	shotCount: detail.shotCount,
	nextCleanShot: detail.nextCleanShot,
	remark: detail.remark,
});

export const Content_EQM4010_R01 = (props: Props) => {
	const { gridRef, detailGridRef } = props;
	useFetchActionbarAuth("EQM4010_R01");
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { setMessage } = useUiuxStore();
	const {
		EQM4010_R01Filter, setEQM4010_R01Filter,
		setEQM4010_R01PastFilter,
		EQM4010_R01List, setEQM4010_R01List,
		selectedEQM4010_R01, setSelectedEQM4010_R01,
		EQM4010_R01DetailList, setEQM4010_R01DetailList,
		setSelectedEQM4010_R01Detail,
		deletedEQM4010_R01DetailList, addDeletedEQM4010_R01DetailList, removeDeletedEQM4010_R01DetailList,
		selectedEQM4010_R01DetailList, setSelectedEQM4010_R01DetailList,
		removeSelectedEQM4010_R01DetailList,
		setOriginalEQM4010_R01DetailList,
		setAllCount,
		detailFetchTrigger, setDetailFetchTrigger,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = useEQM4010_R01Store();
	const { compCdList, employeeCdList } = useCommonCodeStore();

	const resolveCompInfo = useCallback(() => {
		const filterComp = compCdList.find((comp) => comp.compCd === EQM4010_R01Filter.compCd);
		const defaultComp = filterComp ?? compCdList[0];
		return {
			compCd: defaultComp?.compCd ?? '',
			compNm: defaultComp?.compNm ?? '',
		};
	}, [compCdList, EQM4010_R01Filter.compCd]);

	const getDetailParams = useCallback((filter: EQM4010_R01Filter, moldCd?: string) => ({
		moldCd,
		compCd: filter.compCd,
		startDate: filter.inquiryDt?.from ? formatDateToString(new Date(filter.inquiryDt.from)) : '',
		endDate: filter.inquiryDt?.to ? formatDateToString(new Date(filter.inquiryDt.to)) : '',
	}), []);

	const getParams = (filter: EQM4010_R01Filter) => ({
		compCd: filter.compCd,
		moldCd: filter.moldCd,
		moldClass: filter.moldClass,
		startDate: filter.inquiryDt?.from ? formatDateToString(new Date(filter.inquiryDt.from)) : '',
		endDate: filter.inquiryDt?.to ? formatDateToString(new Date(filter.inquiryDt.to)) : '',
		listLength: 0,
	});

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getEQM4010_R01AllCount(getParams(EQM4010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [EQM4010_R01Filter, setAllCount]);

	const handleExcelClick = useCallback(async () => {
		if (!selectedEQM4010_R01) {
			showWarning('선택된 금형이 없습니다.');
			return;
		}

		setLoadingSpinner(true);
		const response = await downloadExcel_EQM4010_R01(
			getDetailParams(EQM4010_R01Filter, selectedEQM4010_R01.moldCd)
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '금형세척 정보.xlsx' });
		setLoadingSpinner(false);
	}, [selectedEQM4010_R01, setLoadingSpinner, EQM4010_R01Filter, getDetailParams]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);

		const response = await getEQM4010_R01(
			'',
			getParams(EQM4010_R01Filter),
			EQM4010_R01List.length ?? 0
		);

		const newList = [...response.data.data];
		setEQM4010_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList?.[0]) {
			gridRefSelectedRowHandler(gridRef, setSelectedEQM4010_R01, () => true);
			setEQM4010_R01PastFilter(EQM4010_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning('조회된 데이터가 없습니다.');
			setSelectedEQM4010_R01(undefined);
			setEQM4010_R01DetailList([]);
			removeDeletedEQM4010_R01DetailList();
			setSelectedEQM4010_R01DetailList([]);
		}
		setLoadingSpinner(false);
		return newList;
	}, [
		EQM4010_R01Filter, setEQM4010_R01List, setEQM4010_R01PastFilter,
		setSelectedEQM4010_R01, setEQM4010_R01DetailList, removeDeletedEQM4010_R01DetailList,
		setSelectedEQM4010_R01DetailList, setLoadingSpinner, setMessage, gridRef,
		renewalAllCount, EQM4010_R01List, setScrollTop, setDetailScrollTop,
	]);

	const defaultEQM4010_R01DetailRow = useCallback((): EQM4010_R01DetailDto => {
		const compInfo = resolveCompInfo();
		const returnData: EQM4010_R01DetailDto = {
			...defaultEQM4010_R01DetailDto,
			...compInfo,
			cleanDt: new Date(),
			shotCount: selectedEQM4010_R01?.currentShot ?? 0,
			inserted: true,
		};

		if (!selectedEQM4010_R01) {
			return returnData;
		}

		return {
			...returnData,
			moldCd: selectedEQM4010_R01.moldCd,
			moldNm: selectedEQM4010_R01.moldNm,
		};
	}, [selectedEQM4010_R01, resolveCompInfo]);

	const fetchDetailList = useCallback(async (moldCd: string) => {
		if (!moldCd) return;

		setLoadingSpinner(true);
		const response = await getEQM4010_R01DetailList(getDetailParams(EQM4010_R01Filter, moldCd));
		const detailList = response.data.data.map((item) => ({
			...item,
			moldNm: item.moldNm || selectedEQM4010_R01?.moldNm || '',
			compNm: item.compNm || compCdList.find((comp) => comp.compCd === item.compCd)?.compNm || '',
			cleanWorkerNm: item.cleanWorkerNm
				|| employeeCdList.find((emp) => emp.empNo === item.cleanWorker)?.empNm
				|| '',
		}));
		setEQM4010_R01DetailList(detailList);

		if (detailList.length > 0) {
			setSelectedEQM4010_R01Detail(detailList[0]);
			setOriginalEQM4010_R01DetailList(detailList);
			detailGridRef.current?.api.ensureIndexVisible(0, 'top');
		} else if (selectedEQM4010_R01) {
			setSelectedEQM4010_R01Detail(undefined);
			setEQM4010_R01DetailList([defaultEQM4010_R01DetailRow()]);
		}
		setLoadingSpinner(false);
		return detailList;
	}, [
		setEQM4010_R01DetailList, setSelectedEQM4010_R01Detail, setLoadingSpinner,
		selectedEQM4010_R01, setOriginalEQM4010_R01DetailList, detailGridRef, defaultEQM4010_R01DetailRow,
		compCdList, employeeCdList, EQM4010_R01Filter, getDetailParams,
	]);

	useEffect(() => {
		if (!detailFetchTrigger) return;
		if (selectedEQM4010_R01?.moldCd) {
			fetchDetailList(selectedEQM4010_R01.moldCd);
			setDetailFetchTrigger(false);
		}

		setMessage(`${selectedEQM4010_R01?.moldNm}의 세척 이력을 조회하였습니다.`);
		setTimeout(() => setMessage(""), 5000);
	}, [selectedEQM4010_R01, fetchDetailList, detailFetchTrigger, setDetailFetchTrigger, setMessage]);

	const initEQM4010_R01Data = useCallback(() => {
		setSelectedEQM4010_R01(undefined);
		setEQM4010_R01DetailList([]);
		removeDeletedEQM4010_R01DetailList();
		setSelectedEQM4010_R01DetailList([]);
	}, [setSelectedEQM4010_R01, setEQM4010_R01DetailList, removeDeletedEQM4010_R01DetailList, setSelectedEQM4010_R01DetailList]);

	const handleDeleteClick = useCallback(() => {
		if (deletedEQM4010_R01DetailList.length > 0) {
			removeDeletedEQM4010_R01DetailList(deletedEQM4010_R01DetailList);
			removeSelectedEQM4010_R01DetailList();
			setEQM4010_R01DetailList([...EQM4010_R01DetailList]);
		} else if (selectedEQM4010_R01DetailList.length > 0) {
			const insertedIds = selectedEQM4010_R01DetailList.filter((value) =>
				EQM4010_R01DetailList.find((item) => item.valueIndex === Number(value))?.inserted === true
			);
			const existingIds = selectedEQM4010_R01DetailList.filter((value) =>
				!insertedIds.includes(value)
				&& !deletedEQM4010_R01DetailList.includes(value)
				&& (
					EQM4010_R01DetailList.find((item) => item.valueIndex === Number(value))?.inserted !== true
					&& EQM4010_R01DetailList.find((item) => item.valueIndex === Number(value))?.isModified !== true
				)
			);

			if (insertedIds.length > 0) {
				const newList = EQM4010_R01DetailList.filter(
					(item) => !insertedIds.includes(item.valueIndex.toString())
				);
				setEQM4010_R01DetailList(newList);
				removeSelectedEQM4010_R01DetailList(insertedIds);
			}

			if (existingIds.length > 0) {
				addDeletedEQM4010_R01DetailList(existingIds);
				removeSelectedEQM4010_R01DetailList(existingIds);
			}
		}
	}, [
		selectedEQM4010_R01DetailList, deletedEQM4010_R01DetailList,
		addDeletedEQM4010_R01DetailList, removeDeletedEQM4010_R01DetailList,
		removeSelectedEQM4010_R01DetailList, setEQM4010_R01DetailList, EQM4010_R01DetailList,
	]);

	const initContainerData = useCallback(() => {
		initData();
		setEQM4010_R01Filter({ ...defaultEQM4010_R01Filter });
	}, [initData, setEQM4010_R01Filter]);

	const handleCreateClick = useCallback(() => {
		if (selectedEQM4010_R01?.moldCd) {
			setEQM4010_R01DetailList([...EQM4010_R01DetailList, defaultEQM4010_R01DetailRow()]);
		} else {
			initEQM4010_R01Data();
		}
	}, [selectedEQM4010_R01, setEQM4010_R01DetailList, EQM4010_R01DetailList, defaultEQM4010_R01DetailRow, initEQM4010_R01Data]);

	const validateInsertData = (detailList: EQM4010_R01DetailDto[]) => {
		if (detailList.length === 0) {
			showWarning('세척 이력을 입력해주세요.');
			return false;
		}

		const cleanDtSet = new Set<string>();

		for (const detail of detailList) {
			if (!detail.moldCd) {
				showWarning('금형코드를 입력해주세요.');
				return false;
			}
			if (!detail.cleanType) {
				showWarning('세척구분을 입력해주세요.');
				return false;
			}
			if (!detail.cleanDt) {
				showWarning('세척일자를 입력해주세요.');
				return false;
			}
			const cleanDtKey = formatDateToString(new Date(detail.cleanDt));
			if (cleanDtSet.has(cleanDtKey)) {
				showWarning('동일한 세척일자가 중복되었습니다.');
				return false;
			}
			cleanDtSet.add(cleanDtKey);
			if (!detail.cleanWorker) {
				showWarning('작업자를 입력해주세요.');
				return false;
			}
			if (detail.shotCount == null) {
				showWarning('세척현재타수를 입력해주세요.');
				return false;
			}
			if (detail.shotCount < (selectedEQM4010_R01?.currentShot ?? 0)) {
				showWarning('세척현재타수는 금형 현재타수보다 작을 수 없습니다.');
				return false;
			}
		}
		return true;
	};

	const handleSaveClick = useCallback(async () => {
		if (!selectedEQM4010_R01) {
			showWarning('선택된 금형이 없습니다.');
			return;
		}

		const newDetailList = EQM4010_R01DetailList.filter((value) =>
			!deletedEQM4010_R01DetailList.includes(value.valueIndex.toString())
		);

		if (!validateInsertData(newDetailList)) return;

		setLoadingSpinner(true);
		try {
			const insertList = newDetailList.filter((detail) => detail.inserted).map(mapDetailForSave);
			const updateList = newDetailList
				.filter((detail) => detail.isModified && !detail.inserted)
				.map(mapDetailForSave);

			const deleteTargets = deletedEQM4010_R01DetailList
				.map((deletedId) => EQM4010_R01DetailList.find((item) => item.valueIndex === Number(deletedId)))
				.filter((detail) => detail && !detail.inserted)
				.map((detail) => mapDetailForSave(detail!));

			const promises = [
				...insertList.map((item) => postEQM4010_R01(item)),
				...updateList.map((item) => patchEQM4010_R01(item)),
				...deleteTargets.map((item) => deleteEQM4010_R01(item.moldCleanNo)),
			];

			if (promises.length > 0) {
				await Promise.all(promises);
			}

			showSuccess('금형 세척 이력 저장이 완료되었습니다.');
			removeDeletedEQM4010_R01DetailList();
			setSelectedEQM4010_R01DetailList([]);

			const moldCurrentShotMap = new Map<string, number>();
			newDetailList.forEach((detail) => {
				if (!detail.moldCd) return;
				const prevShotCount = moldCurrentShotMap.get(detail.moldCd);
				if (prevShotCount == null || detail.shotCount > prevShotCount) {
					moldCurrentShotMap.set(detail.moldCd, detail.shotCount);
				}
			});

			const savedMoldCd = selectedEQM4010_R01.moldCd;
			const fetchedList = await fetchData();
			const updatedList = (fetchedList ?? []).map((row) => {
				const shotCount = moldCurrentShotMap.get(row.moldCd);
				return shotCount != null ? { ...row, currentShot: shotCount } : row;
			});
			const updatedSelected = updatedList.find((row) => row.moldCd === savedMoldCd);

			setEQM4010_R01List(updatedList);
			if (updatedSelected) {
				useEQM4010_R01Store.setState({ selectedEQM4010_R01: updatedSelected });
			}

			if (savedMoldCd) {
				await fetchDetailList(savedMoldCd);
			}
		} catch (error) {
			showWarning('저장 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		selectedEQM4010_R01, EQM4010_R01DetailList, deletedEQM4010_R01DetailList,
		fetchData, fetchDetailList, setEQM4010_R01List, setLoadingSpinner, removeDeletedEQM4010_R01DetailList,
		setSelectedEQM4010_R01DetailList,
	]);

	return {
		handleExcelClick,
		fetchData,
		fetchDetailList,
		defaultEQM4010_R01DetailRow,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	};
};
